//© all rights reserved to 6over6 Ltd. 2022. This software (the "Software") is
the property of 6over6 Ltd. and its licensors. Any use of the Software is
strictly prohibited unless an explicit license or permission is granted by
6over6 Ltd.
<template>
  <BackgroundColorOverwrite :backgroundColor="'#fff'" class="go_background">
    <div class="go_qr_container">
      <TitleSection :showSubtitle="false" />
      <section class="go_layout_change_section">
        <section>
          <AppStoreLogo
            v-if="isMobile"
            :branchUrl="branchUrl"
            :showMyRxApp="true"
          />
          <GetTheAppSection
            v-if="!isMobile"
            :startAnimation="startAnimation"
            :branchUrl="branchUrl"
            :appIconSrc="appIconSrc"
            :storeLinks="store.state.page.store_links.go_lenses"
          />
        </section>
      </section>
      <section v-if="isMobile" class="go_have_app">
        {{ store.state.page.mobile_have_app_title }}<br />
        <a :href="branchUrl" target="_blank" :style="getLinkStyle">{{
          store.state.page.mobile_have_app_url
        }}</a>
      </section>
      <WhatYouNeedSection />
      <div class="go_steps_wrapper">
        <StepsSection
          :stepTestImg="stepTestImg"
          :stepGetPrescriptionImg="stepGetPrescriptionImg"
          :stepStartShopping="stepStartShopping"
        />
      </div>
      <FaqSection />
      <section>
        <div v-if="!isMobile" class="go_footer">
          <QR
            v-if="qrInfo"
            class="go_qr"
            :qrInfo="qrInfo"
            :qrWidth="footerQrWidth"
            :startAnimation="startAnimation"
          ></QR>
          <div class="go_footer_instruction">
            {{ store.state.page.qr_instruction_bottom }}
          </div>
        </div>
        <div v-else class="go_footer_mobile">
          <AppStoreLogo :branchUrl="branchUrl" :showMyRxApp="true" />
          <div class="download_now">
            {{ store.state.page.mobile_download_now }}
          </div>
        </div>
      </section>
    </div>
  </BackgroundColorOverwrite>
</template>
<script setup>
import Utils from "@/../../../common/services/Utils.js";
import AnalyticsEvent from "@/../../../common/models/AnalyticsEvent.js";

import BackgroundColorOverwrite from "@/components/BackgroundColorOverwrite.vue";
import TitleSection from "@/components/sections/qrScanPages/TitleElm.vue";
import GetTheAppSection from "@/components/sections/qrScanPages/GetTheApp.vue";
import QR from "@/components/sections/qrScanPages/QrDisplay.vue";
import StepsSection from "@/components/sections/qrScanPages/StepsSection.vue";
import WhatYouNeedSection from "@/components/sections/qrScanPages/WhatYouNeed.vue";
import FaqSection from "@/components/sections/qrScanPages/FAQSection.vue";
import AppStoreLogo from "@/components/icons/AppStore.vue";
import branch from "branch-sdk";

import { useStore } from "vuex";
import { defineProps, ref, inject, computed, onMounted } from "vue";

defineProps(["startAnimation"]);

const store = useStore();
const api = inject("api");

const branchUrl = ref(null);
const isMobile = ref(Utils.isMobile(true));
const appIconSrc = ref(
  "https://assets.glasseson.com/web/images/luna/v1/myrx_icon_76.png"
);
const stepTestImg = ref(
  "https://assets.glasseson.com/web/images/luna/v1/scan_your_glasses_circle.png"
);

const stepGetPrescriptionImg = ref(
  "https://assets.glasseson.com/web/images/luna/v1/get_your_prescription_circle.png"
);

const stepStartShopping = ref(
  "https://assets.glasseson.com/web/images/luna/v1/start_shopping_circle.png"
);

const footerQrWidth = ref(210);

async function getBranchUrl() {
  const branch_key = store.state.page.deep_link_config["go_lenses"].branch_key;
  const data_to_send = {
    app_id: store.state.page.deep_link_config["go_lenses"].app_id,
    data: {
      $__is_onboarding_link:
        store.state.page.deep_link_config["go_lenses"].is_onboaring_link,
      client_id: store.state.Session.clientId,
      profile_name: store.state.Session.options.profileName,
      socket_id: isMobile.value
        ? "mobile page no socket"
        : store.state.Session.socketId,
      locale: store.state.page.locale,
      theme: api.config.appConfig.theme,
    },
  };
  try {
    let options = { tracking_disabled: true };
    branch.init(branch_key, options, (error) => {
      if (!error) {
        branch.link(data_to_send, function (error, link) {
          if (error) {
            console.error(error);
          }
          branchUrl.value = !error ? link : null;
        });
      } else {
        console.error(error);
      }
    });
  } catch (error) {
    api.logger.log(
      `There was an error connecting branch io! error: ${error}`,
      "WEB_COMPANION"
    );
  }
}

getBranchUrl();

const getLinkStyle = computed(function () {
  return {
    color: store.state.Style.themeColor,
  };
});

const qrInfo = computed(function () {
  return branchUrl.value;
});

onMounted(function () {
  api.analytics.log(new AnalyticsEvent("pv web wc hub default", "pv", "good"));
});
</script>

<style lang="scss" scoped>
section {
  width: 100%;
}
.go_background {
  touch-action: auto !important;
  width: 100%;
  min-height: 100%;
  overflow: scroll;
  padding: 0 !important;
  justify-content: flex-start !important;
}
.go_qr_container {
  width: 100%;
  text-align: center;
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-bottom: 200px;
  padding: 100px 0px !important;
  @include mobile {
    padding: 65px 0px !important;
  }

  .go_title_section {
    width: 986px;

    @include tablet {
      width: 811px;
    }
    @include mobile {
      width: 300px;
    }
  }

  .go_layout_change_section {
    display: flex;
    flex-direction: row;
    justify-content: center;

    @include mobile {
      flex-direction: column;
    }

    @include tablet {
      flex-direction: column;
    }

    .go_title_image {
      display: flex;
      margin-top: 0;
      justify-content: center;

      @include desktop {
        margin-top: -100px;
        justify-content: flex-end;
      }

      @include desktop_small {
        margin-top: -50px;
      }

      .go_frame_one {
        width: 637px;
        @include mobile {
          width: 330px;
          margin-left: -25px;
        }

        @include tablet {
          width: 400px;
          margin-left: -80px;
        }

        @include desktop_small {
          width: 421px;
        }
      }
    }
  }

  .go_steps_wrapper {
    height: 600px;

    @include tablet {
      height: 440px;
    }

    @include mobile {
      height: 100%;
    }
  }

  .go_have_app {
    font-size: 24px;
    line-height: 36px;
    margin-top: 55px;
  }

  .go_footer {
    display: flex;
    flex-direction: row;
    margin-top: 93px;
    align-items: center;
    justify-content: center;

    @include tablet {
      margin-top: 50px;
    }

    .go_footer_instruction {
      font-weight: 300;
      font-size: 29.2px;
      line-height: 41px;
      width: 518px;
      margin-left: 80px;

      @include tablet {
        font-size: 24px;
        line-height: 34px;
        width: 418px;
      }
    }

    .go_qr {
      width: 210px;

      @include tablet {
        width: 170px;
      }
    }
  }

  .go_footer_mobile {
    display: flex;
    flex-direction: column;
    margin-top: 43px;
    align-items: center;
    width: 100%;

    .download_now {
      margin-top: 35px;
      font-size: 21px;
      line-height: 34px;
    }
  }
}
</style>
